/**
 * useSigningRequest — wallet-agnostic signing resolution (multi-wallet support, Phase 2).
 *
 * Components pass a normalized SigningPayload and get back SigningState + callbacks,
 * without knowing how the signature gets resolved. Today every payload is delivered
 * by Xaman, so this delegates to useXamanPayloadStatus (WebSocket → polling → dispatch
 * verification) unchanged.
 *
 * When a client-signing provider (WalletConnect / Joey) lands, resolution dispatches on
 * payload.provider. Hooks can't branch conditionally, so the plan of record is
 * per-provider resolution components rendered by WalletSigningModal (e.g.
 * <XamanResolution> wrapping this hook vs <WalletConnectResolution> awaiting the
 * session promise) — NOT a growing conditional inside this hook.
 */

import { useXamanPayloadStatus } from './useXamanPayloadStatus';
import type { SigningPayload, SigningState } from '../types/signing';

export interface UseSigningRequestOptions {
  payload: SigningPayload;
  onSigned?: (txid: string) => void;
  onRejected?: () => void;
  onExpired?: () => void;
  /** Override polling cadence (long-running parallel flows pass higher attempt caps). */
  pollingInterval?: number;
  maxPollingAttempts?: number;
}

export function useSigningRequest({
  payload,
  onSigned,
  onRejected,
  onExpired,
  pollingInterval,
  maxPollingAttempts,
}: UseSigningRequestOptions): SigningState {
  // Xaman is the only delivery provider in Phase 2 (payload.provider is always 'xaman').
  return useXamanPayloadStatus({
    uuid: payload.id,
    websocketUrl: payload.websocketUrl,
    onSigned,
    onRejected,
    onExpired,
    ...(pollingInterval !== undefined ? { pollingInterval } : {}),
    ...(maxPollingAttempts !== undefined ? { maxPollingAttempts } : {}),
  });
}
